/**
 * Image-SEO helpers for product photos.
 *
 * Google Images ranks a photo on the words around it: the `alt`, the
 * filename, and the page it sits on. The filename isn't ours to set —
 * `Image` exposes no `name`/`filename` field, and the media service
 * decides the URL. It already emits descriptive ones for newer uploads
 * (`/producten/<id>/<id>/brink-campman-scone-tapijt.jpeg`) while ~88% of
 * live listings are still on the legacy opaque form
 * (`/image/<uuid>/original.jpg`) — backfilling those is a media-service
 * job, tracked separately. `alt` is the lever we own in this repo.
 *
 * The old alt (`Image 1 of {title}`) opened with two stopwords and added
 * nothing the <title> didn't already say. This builds
 * `{title}, {maker}, {object}, {material}, {colour}, {era}` instead,
 * skipping any token the product title already contains so we never emit
 * "Eames … Eames".
 *
 * WHO-6188: the words are in the page locale (title, category and
 * attribute titles come from the gateway already localized; the photo
 * suffix and the fallback are localized here) and the output carries no
 * dashes: web copy must not, and the old joiner put one in every alt and
 * every JSON-LD image name/caption.
 */
import { toContentLocale } from "@/i18n/config";

type ImageAltAttribute = {
  __typename?: string | null;
  title?: string | null;
};

type ImageAltCategory = {
  title?: string | null;
  slug?: string | null;
  level?: number | null;
};

export type ImageAltProduct = {
  title?: string | null;
  brand_suggestion?: string | null;
  // Callers pass PartialDeep-typed products, whose array items are
  // `T | undefined` — hence the `undefined` in the element unions.
  attributes?: ReadonlyArray<ImageAltAttribute | null | undefined> | null;
  categories?: ReadonlyArray<ImageAltCategory | null | undefined> | null;
};

/** Google truncates alt text well before this; keep photos scannable. */
const MAX_ALT_LENGTH = 125;

const DEFAULT_ALT_LOCALE = "en";

type AltCopy = {
  photo: (position: number, total: number) => string;
  fallback: string;
};

/**
 * Kept here rather than in Lokalise: the same words are emitted server-side
 * into JSON-LD, where no i18n instance is available, and they must match
 * the rendered alt exactly.
 */
const ALT_COPY: Record<string, AltCopy> = {
  en: {
    photo: (position, total) =>
      total > 1 ? `photo ${position} of ${total}` : `photo ${position}`,
    fallback: "Second-hand design piece on Whoppah",
  },
  nl: {
    photo: (position, total) =>
      total > 1 ? `foto ${position} van ${total}` : `foto ${position}`,
    fallback: "Tweedehands designstuk op Whoppah",
  },
  de: {
    photo: (position, total) =>
      total > 1 ? `Foto ${position} von ${total}` : `Foto ${position}`,
    fallback: "Gebrauchtes Designstück auf Whoppah",
  },
  fr: {
    photo: (position, total) =>
      total > 1 ? `photo ${position} sur ${total}` : `photo ${position}`,
    fallback: "Pièce design de seconde main sur Whoppah",
  },
  es: {
    photo: (position, total) =>
      total > 1 ? `foto ${position} de ${total}` : `foto ${position}`,
    fallback: "Pieza de diseño de segunda mano en Whoppah",
  },
  it: {
    photo: (position, total) =>
      total > 1 ? `foto ${position} di ${total}` : `foto ${position}`,
    fallback: "Pezzo di design di seconda mano su Whoppah",
  },
};

const altCopy = (locale: string | undefined): AltCopy =>
  ALT_COPY[toContentLocale(locale)] ??
  (ALT_COPY[DEFAULT_ALT_LOCALE] as AltCopy);

/**
 * Seller titles use spaced en/em dashes as separators; those become a
 * comma so no alt or JSON-LD caption carries one. An unspaced one becomes
 * a hyphen. Hyphens in model numbers ("BW-170-1001") are part of the name
 * and stay.
 */
const SPACED_DASH = /\s+[\u2013\u2014]\s+/g;
const BARE_DASH = /[\u2013\u2014]/g;
const withoutDashes = (text: string) =>
  text.replace(SPACED_DASH, ", ").replace(BARE_DASH, "-");

const attributeTitles = (
  product: ImageAltProduct | null | undefined,
  typename: string
) =>
  (product?.attributes ?? [])
    .filter(attribute => attribute?.__typename === typename)
    .map(attribute => attribute?.title?.trim())
    .filter((title): title is string => !!title);

const firstAttributeTitle = (
  product: ImageAltProduct | null | undefined,
  typename: string
) => attributeTitles(product, typename)[0];

/**
 * The most specific category ("Lounge chairs", not "Furniture") is the
 * object noun a searcher actually types. PDP categories carry a
 * localized `title`; the PLP overview fragment only has `slug`, so fall
 * back to de-slugging it.
 */
const objectNoun = (product: ImageAltProduct | null | undefined) => {
  const deepest = (product?.categories ?? [])
    .filter((category): category is ImageAltCategory => !!category)
    .reduce<ImageAltCategory | undefined>(
      (best, category) =>
        (category.level ?? 0) >= (best?.level ?? -1) ? category : best,
      undefined
    );

  const label = deepest?.title?.trim() || deepest?.slug?.replace(/-/g, " ");
  return label?.trim() || undefined;
};

/**
 * Category names are plural ("Lounge chairs") while titles are singular
 * ("Eames Lounge Chair"), so a plain substring check misses the overlap.
 * Dropping a trailing "s" catches it without pulling in a pluralizer.
 */
const alreadySaid = (lowercaseTitle: string, token: string) => {
  const lower = token.toLowerCase();
  return (
    lowercaseTitle.includes(lower) ||
    lowercaseTitle.includes(lower.replace(/s$/, ""))
  );
};

/**
 * The Material taxonomy contains bucket labels, not words anyone searches
 * — "Biopolymer and other sustainable materials" is 41 chars of the alt
 * budget for zero query value. Real materials are one or two short words.
 */
const isSearchableToken = (token: string) =>
  token.length <= 20 && token.split(/\s+/).length <= 2;

const truncateAtWord = (text: string, max: number) => {
  if (text.length <= max) {
    return text;
  }

  const clipped = text.slice(0, max);
  const lastSpace = clipped.lastIndexOf(" ");
  return (lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped).trim();
};

/**
 * Descriptive alt text for image `index` of a product's gallery, in `locale`.
 *
 * The primary image (index 0) gets no "photo n" suffix — it's the one
 * Google is most likely to surface, so it stays clean. Secondary images
 * get the suffix so every alt on the page is unique.
 */
export const buildProductImageAlt = (
  product: ImageAltProduct | null | undefined,
  index = 0,
  total = 0,
  locale?: string
) => {
  const copy = altCopy(locale);
  const title = withoutDashes(product?.title?.trim() ?? "");

  const maker =
    firstAttributeTitle(product, "Brand") ??
    firstAttributeTitle(product, "Designer") ??
    firstAttributeTitle(product, "Artist") ??
    product?.brand_suggestion?.trim();

  const materials = attributeTitles(product, "Material")
    .filter(isSearchableToken)
    .slice(0, 2);
  const colour = firstAttributeTitle(product, "Color");
  const era = firstAttributeTitle(product, "Style");

  const lowercaseTitle = title.toLowerCase();
  const seen = new Set<string>();
  const descriptors = [maker, objectNoun(product), ...materials, colour, era]
    .filter((token): token is string => !!token)
    .filter(token => {
      const lower = token.toLowerCase();
      // Drop anything the title (or an earlier descriptor) already says.
      if (alreadySaid(lowercaseTitle, token) || seen.has(lower)) {
        return false;
      }
      seen.add(lower);
      return true;
    });

  const suffix = index > 0 ? ` (${copy.photo(index + 1, total)})` : "";
  const budget = MAX_ALT_LENGTH - suffix.length;

  // Titles are prose, so clipping one mid-sentence still reads fine.
  // Descriptors are discrete keywords: a half-written one (", Bielefelder")
  // is noise, so drop whole tokens that don't fit rather than cut them.
  // Everything is joined with a comma: no dash ever reaches web copy.
  const head = truncateAtWord(title, budget);
  let body = head;
  for (const descriptor of descriptors) {
    const token = withoutDashes(descriptor);
    const candidate = body ? `${body}, ${token}` : token;
    if (candidate.length > budget) {
      break;
    }
    body = candidate;
  }

  return (body || copy.fallback) + suffix;
};
